import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { toast } from "@performyard/py-components";
import {
	GetMeetingsIntegrationsAvailableProvidersResponse,
	GetMeetingsIntegrationsSettingsResponse,
	GetUserCalendarIntegrationStatusResponse,
	GetUserVideoConferencingStatusResponse,
	InitiateCalendarIntegrationOAuthFlowResponse,
	InitiateVideoConferencingOAuthFlowResponse,
	PatchMeetingsIntegrationsSettingsRequest,
	SaveSelectedCalendarRequest,
	VIDEO_CONFERENCING_PROVIDERS,
	VIDEO_CONFERENCING_PROVIDER_DISPLAY_NAMES,
	VIDEO_CONFERENCING_PROVIDER_EPHEMERAL_FLAGS,
	VideoConferencingProviderEnum,
} from "@performyard/service-contracts";

import { getFeatureFlagQuery, useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

import {
	meetingsIntegrationsAvailableProvidersQuery,
	meetingsIntegrationsSettingsQuery,
	userCalendarIntegrationStatusQuery,
	userCalendarsQuery,
	userVideoConferencingStatusQuery,
} from "../../queries";
import {
	disconnectCalendarIntegration,
	disconnectVideoConferencingIntegration,
	getCalendarIntegrationOAuthUrl,
	getVideoConferencingOAuthUrl,
	saveSelectedCalendar,
	updateMeetingsIntegrationsSettings,
} from "../../services/defs/internal-api";
import { PYUseQueryOptions } from "../StandardQueryArguments";

export const useMeetingsIntegrationsSettings = (
	options?: PYUseQueryOptions<GetMeetingsIntegrationsSettingsResponse>
) => {
	const query = useQuery({
		...meetingsIntegrationsSettingsQuery(),
		...options,
	});

	return {
		data: query?.data,
		isLoading: query?.isLoading,
	};
};

export const useUpdateMeetingsIntegrationsSettings = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: PatchMeetingsIntegrationsSettingsRequest) => updateMeetingsIntegrationsSettings(data),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["meetings-integrations-settings"] });
		},
	});
};

export const useCalendarIntegrationOAuthUrl = ({ provider }: { provider: "google" | "outlook" }) => {
	const {
		mutateAsync,
		isPending: isLoading,
		error,
	} = useMutation<InitiateCalendarIntegrationOAuthFlowResponse>({
		mutationFn: () => getCalendarIntegrationOAuthUrl({ provider }),
		onSuccess: (data) => {
			// When we get the auth URL, redirect the user to it
			if (data.authUrl) {
				window.location.href = data.authUrl;
			}
		},
	});

	return {
		initiateCalendarIntegrationAuth: mutateAsync,
		isLoading,
		error,
	};
};

export const useUserCalendarIntegrationStatus = (
	options?: PYUseQueryOptions<GetUserCalendarIntegrationStatusResponse>
) => {
	const query = useQuery({
		...userCalendarIntegrationStatusQuery(),
		...options,
	});

	return {
		data: query?.data,
		isLoading: query?.isLoading,
		refetch: query?.refetch,
	};
};

export const useDisconnectCalendarIntegration = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			return disconnectCalendarIntegration();
		},
		onSuccess: async () => {
			// Invalidate and refetch user calendar status after successful disconnect
			await queryClient.invalidateQueries({ queryKey: userCalendarIntegrationStatusQuery().queryKey });
			toast.success("Calendar Successfully Disconnected");
		},
		onError: (error) => {
			console.error("Error Disconnecting Calendar:", error);
		},
		// Global error handler surfaces the backend message, falling back to this when none is sent.
		meta: { errorMessage: "Failed to Disconnect Calendar" },
	});
};

/**
 * Unified hook to get user calendars from any provider
 */
export const useUserCalendars = ({
	provider,
	enabled = true,
}: {
	provider: "google" | "outlook";
	enabled?: boolean;
}) => {
	const query = useQuery({
		...userCalendarsQuery(provider),
		enabled,
	});

	const data = query?.error ? { calendars: [] } : query?.data;

	return {
		data,
		isLoading: query?.isLoading,
		refetch: query?.refetch,
		error: query?.error,
	};
};

export const useSaveSelectedCalendar = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: SaveSelectedCalendarRequest) => saveSelectedCalendar(data),
		onSuccess: async () => {
			// Invalidate and refetch user calendar status after successful save
			await queryClient.invalidateQueries({ queryKey: userCalendarIntegrationStatusQuery().queryKey });
			toast.success("Calendar Successfully Changed");
		},
		onError: async (error) => {
			console.error("Error Changing Calendar:", error);
			// Saving a calendar runs through ensureValidAccessToken, which can clear a dead connection
			// server-side; resync status so the card/modal reflect the disconnect.
			await queryClient.invalidateQueries({ queryKey: userCalendarIntegrationStatusQuery().queryKey });
		},
		// Global error handler surfaces the backend message, falling back to this when none is sent.
		meta: { errorMessage: "Failed to Change Calendar" },
	});
};

// ---------- Video Conferencing ----------

/**
 * Whether the video conferencing feature is flag-enabled for the company — the meetings
 * integrations FF plus at least one provider's ephemeral meetings module FF. The single gate
 * for every video-conferencing affordance and for the provider-list query (which 403s otherwise).
 * Mirrors the backend pre-handler gate; when the ephemeral provider FFs are removed, drop them here too.
 */
export const useVideoConferencingFeatureEnabled = (): boolean => {
	const { value: meetingsFFOn } = useFeatureFlagQuery("ff-is-meetings-integrations-enabled", false);
	const { anyEnabled } = useVideoConferencingProviderFlags();
	return !!meetingsFFOn && anyEnabled;
};

/**
 * All provider ephemeral flags in one read, derived from the canonical provider list. `byProvider`
 * gates a specific provider's affordances; `anyEnabled` gates "is any VC provider live." Reads the
 * shared feature-flags query once (a hook can't be called per-provider in a loop).
 */
export const useVideoConferencingProviderFlags = (): {
	byProvider: Record<VideoConferencingProviderEnum, boolean>;
	anyEnabled: boolean;
} => {
	const { data } = useQuery(getFeatureFlagQuery());
	const flags = data?.feature_flags ?? [];
	const byProvider = Object.fromEntries(
		VIDEO_CONFERENCING_PROVIDERS.map((provider) => [
			provider,
			flags.find((flag) => flag._id === VIDEO_CONFERENCING_PROVIDER_EPHEMERAL_FLAGS[provider])?.value === true,
		])
	) as Record<VideoConferencingProviderEnum, boolean>;
	return { byProvider, anyEnabled: Object.values(byProvider).some(Boolean) };
};

/** Fetches the server-driven list of video-conferencing providers. */
export const useMeetingsIntegrationsAvailableProviders = (
	options?: PYUseQueryOptions<GetMeetingsIntegrationsAvailableProvidersResponse>
) => {
	const query = useQuery({
		...meetingsIntegrationsAvailableProvidersQuery(),
		...options,
	});

	return {
		data: query?.data,
		isLoading: query?.isLoading,
	};
};

/**
 * Resolves a provider's short display name (e.g. "Teams") from the static provider map. Returns ""
 * for an unset/unknown provider. Intentionally static (no query) so the name still resolves when the
 * company has video conferencing disabled — the providers endpoint 403s in that case.
 */
export const getVideoConferencingProviderShortName = (provider?: string | null): string => {
	if (!provider) return "";
	return VIDEO_CONFERENCING_PROVIDER_DISPLAY_NAMES[provider as VideoConferencingProviderEnum]?.short ?? "";
};

/** Human list of all providers by long name, e.g. "Zoom or Microsoft Teams". For connect-prompt copy. */
export const getVideoConferencingProviderListText = (): string => {
	const names = VIDEO_CONFERENCING_PROVIDERS.map((p) => VIDEO_CONFERENCING_PROVIDER_DISPLAY_NAMES[p].long);
	if (names.length <= 1) return names.join("");
	return `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
};

/** The user's current video-conferencing connection, or null if not connected. */
export const useUserVideoConferencingStatus = (
	options?: PYUseQueryOptions<GetUserVideoConferencingStatusResponse> & { includeUserSettings?: boolean }
) => {
	const { includeUserSettings, ...queryOptions } = options ?? {};
	const query = useQuery({
		...userVideoConferencingStatusQuery(includeUserSettings),
		...queryOptions,
	});

	return {
		data: query?.data,
		isLoading: query?.isLoading,
		isFetching: query?.isFetching,
		refetch: query?.refetch,
	};
};

/** Initiates a same-tab OAuth flow; on success navigates to the returned authUrl. */
export const useVideoConferencingOAuthUrl = ({ provider }: { provider: VideoConferencingProviderEnum }) => {
	const {
		mutateAsync,
		isPending: isLoading,
		error,
	} = useMutation<InitiateVideoConferencingOAuthFlowResponse>({
		mutationFn: () => getVideoConferencingOAuthUrl({ provider }),
		onSuccess: (data) => {
			if (data.authUrl) {
				window.location.href = data.authUrl;
			}
		},
	});

	return {
		initiateVideoConferencingAuth: mutateAsync,
		isLoading,
		error,
	};
};

export const useDisconnectVideoConferencingIntegration = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ provider }: { provider: VideoConferencingProviderEnum }) => {
			return disconnectVideoConferencingIntegration({ provider });
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["user-video-conferencing-status"] });
			toast.success("Successfully Disconnected");
		},
		onError: (error) => {
			console.error("Error disconnecting video conferencing integration:", error);
			toast.error("Failed to disconnect");
		},
	});
};
